import React, { useState } from 'react';
import {
  CheckCircle, Warning, ShieldWarning, Funnel, Factory
} from '@phosphor-icons/react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ScatterChart, Scatter, ZAxis, Cell
} from 'recharts';

const STATUS_COLORS = {
  'Actual Verified': '#4ade80',
  'Actual Unverified': '#facc15',
  'Default Applied': '#ef4444',
};

const SECTOR_COLORS = {
  'Iron and Steel': '#f97316',
  'Aluminium': '#60a5fa',
  'Chemicals': '#a78bfa',
};

function StatusBadge({ verified, actual }) {
  if (verified) return <span className="inline-flex items-center gap-1 text-emerald-400 text-[10px] font-semibold uppercase"><CheckCircle size={12} weight="fill" /> Verified</span>;
  if (actual) return <span className="inline-flex items-center gap-1 text-amber-400 text-[10px] font-semibold uppercase"><Warning size={12} weight="fill" /> Unverified</span>;
  return <span className="inline-flex items-center gap-1 text-red-400 text-[10px] font-semibold uppercase"><ShieldWarning size={12} weight="fill" /> Default</span>;
}

export default function CbamProducts({ data }) {
  const [sectorFilter, setSectorFilter] = useState('all');
  const [scopeFilter, setScopeFilter] = useState('all');
  if (!data) return null;

  const products = (data.products || []).filter(p => {
    if (sectorFilter !== 'all' && p.sector !== sectorFilter) return false;
    if (scopeFilter === 'in_scope' && !p.cbam_scope_flag) return false;
    if (scopeFilter === 'future' && !p.cbam_future_scope_flag) return false;
    return true;
  });

  const sectors = [...new Set((data.products || []).map(p => p.sector))];

  // Heatmap bar data
  const heatData = products.map(p => ({
    name: p.description?.length > 22 ? p.description.slice(0, 22) + '...' : p.description,
    intensity: p.total_intensity,
    default: p.sector_default_intensity,
    fill: SECTOR_COLORS[p.sector] || '#6b7280',
  }));

  // Bubble chart data (volume vs intensity vs revenue)
  const bubbleData = products.filter(p => p.cbam_scope_flag).map(p => ({
    x: p.annual_eu_volume_tonnes,
    y: p.total_intensity,
    z: p.annual_eu_revenue_eur,
    name: p.description,
    fill: SECTOR_COLORS[p.sector] || '#6b7280',
  }));

  return (
    <div className="space-y-5 animate-fade-in" data-testid="cbam-products">
      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center" data-testid="cbam-product-filters">
        <div className="flex items-center gap-1.5 text-xs font-body text-text-secondary">
          <Funnel size={14} />
          <span>Filters:</span>
        </div>
        <select
          value={sectorFilter}
          onChange={e => setSectorFilter(e.target.value)}
          className="bg-surface border border-border rounded-md px-3 py-1.5 text-xs font-body text-text-primary focus:outline-none focus:border-primary"
          data-testid="cbam-sector-filter"
        >
          <option value="all">All Sectors</option>
          {sectors.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select
          value={scopeFilter}
          onChange={e => setScopeFilter(e.target.value)}
          className="bg-surface border border-border rounded-md px-3 py-1.5 text-xs font-body text-text-primary focus:outline-none focus:border-primary"
          data-testid="cbam-scope-filter"
        >
          <option value="all">All Scope</option>
          <option value="in_scope">In Scope Now</option>
          <option value="future">Future Scope</option>
        </select>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Intensity Heatmap */}
        <div className="bg-surface border border-border rounded-lg p-5" data-testid="cbam-intensity-heatmap">
          <h3 className="text-sm font-body font-semibold text-text-primary mb-1">Emissions Intensity vs Sector Default</h3>
          <p className="text-[11px] font-body text-text-secondary mb-4">tCO2e per tonne product — actual vs CBAM default</p>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={heatData} layout="vertical" margin={{ left: 10, right: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" horizontal={false} />
              <XAxis type="number" tick={{ fill: '#94a3b8', fontSize: 11 }} />
              <YAxis type="category" dataKey="name" tick={{ fill: '#94a3b8', fontSize: 10 }} width={130} />
              <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, fontSize: 12 }} />
              <Bar dataKey="intensity" name="Actual" radius={[0, 4, 4, 0]} barSize={12}>
                {heatData.map((e, i) => <Cell key={i} fill={e.fill} />)}
              </Bar>
              <Bar dataKey="default" name="Sector Default" fill="#475569" radius={[0, 4, 4, 0]} barSize={12} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Bubble Chart */}
        <div className="bg-surface border border-border rounded-lg p-5" data-testid="cbam-bubble-chart">
          <h3 className="text-sm font-body font-semibold text-text-primary mb-1">Volume vs Intensity (Bubble = Revenue)</h3>
          <p className="text-[11px] font-body text-text-secondary mb-4">In-scope products only — bigger bubble = higher EU revenue</p>
          <ResponsiveContainer width="100%" height={220}>
            <ScatterChart margin={{ left: 0, right: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis type="number" dataKey="x" name="Volume (t)" tick={{ fill: '#94a3b8', fontSize: 11 }} label={{ value: 'Volume (t)', position: 'insideBottom', offset: -5, fill: '#64748b', fontSize: 10 }} />
              <YAxis type="number" dataKey="y" name="Intensity" tick={{ fill: '#94a3b8', fontSize: 11 }} label={{ value: 'tCO2e/t', angle: -90, position: 'insideLeft', fill: '#64748b', fontSize: 10 }} />
              <ZAxis type="number" dataKey="z" range={[80, 600]} />
              <Tooltip
                contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, fontSize: 12 }}
                formatter={(val, name) => {
                  if (name === 'Volume (t)') return [`${val} t`, name];
                  if (name === 'Intensity') return [`${val} tCO2e/t`, name];
                  return [val, name];
                }}
              />
              <Scatter data={bubbleData}>
                {bubbleData.map((e, i) => <Cell key={i} fill={e.fill} fillOpacity={0.7} stroke={e.fill} strokeWidth={1} />)}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Product Table */}
      <div className="bg-surface border border-border rounded-lg p-5" data-testid="cbam-product-table">
        <h3 className="text-sm font-body font-semibold text-text-primary mb-4">Product Exposure Detail ({products.length} products)</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-xs font-body">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-2 text-text-secondary font-semibold">Product</th>
                <th className="text-left py-2 text-text-secondary font-semibold">CN Code</th>
                <th className="text-left py-2 text-text-secondary font-semibold">Sector</th>
                <th className="text-left py-2 text-text-secondary font-semibold">Route</th>
                <th className="text-right py-2 text-text-secondary font-semibold">Intensity</th>
                <th className="text-right py-2 text-text-secondary font-semibold">vs Default</th>
                <th className="text-right py-2 text-text-secondary font-semibold">EU Volume</th>
                <th className="text-right py-2 text-text-secondary font-semibold">Net CBAM</th>
                <th className="text-center py-2 text-text-secondary font-semibold">Scope</th>
                <th className="text-center py-2 text-text-secondary font-semibold">Data Status</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p, i) => (
                <tr key={i} className="border-b border-border/50 hover:bg-white/[0.02]">
                  <td className="py-2.5 text-text-primary font-medium max-w-[180px] truncate">{p.description}</td>
                  <td className="py-2.5 text-text-secondary font-mono text-[10px]">{p.cn_code}</td>
                  <td className="py-2.5"><span className="px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: (SECTOR_COLORS[p.sector] || '#6b7280') + '20', color: SECTOR_COLORS[p.sector] || '#6b7280' }}>{p.sector}</span></td>
                  <td className="py-2.5 text-text-secondary text-[10px]">{p.route_code}</td>
                  <td className="py-2.5 text-right font-semibold text-text-primary">{p.total_intensity}</td>
                  <td className={`py-2.5 text-right font-semibold ${p.delta_vs_default_pct < 0 ? 'text-emerald-400' : p.delta_vs_default_pct > 0 ? 'text-red-400' : 'text-text-secondary'}`}>
                    {p.delta_vs_default_pct > 0 ? '+' : ''}{p.delta_vs_default_pct}%
                  </td>
                  <td className="py-2.5 text-right text-text-secondary">{p.eu_shipped_tonnes}t / {p.eu_shipment_count}</td>
                  <td className="py-2.5 text-right font-semibold text-amber-400">EUR {p.net_cbam_eur?.toLocaleString()}</td>
                  <td className="py-2.5 text-center">
                    {p.cbam_scope_flag ? (
                      <span className="text-[10px] font-semibold text-red-400 bg-red-500/10 px-2 py-0.5 rounded-full">IN SCOPE</span>
                    ) : p.cbam_future_scope_flag ? (
                      <span className="text-[10px] font-semibold text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded-full">2028 WATCH</span>
                    ) : (
                      <span className="text-[10px] font-semibold text-text-secondary">Exempt</span>
                    )}
                  </td>
                  <td className="py-2.5 text-center"><StatusBadge verified={p.verified_flag} actual={p.actual_flag} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
